
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';

import { sharedState } from '../../../../framework/state';
import { postResource } from '../../../../framework/client/resource';
import { getDatabaseOptions, OptionValue } from '../../../../framework/service/options';
import { FileMeta } from '../../../../../common/application/model/FileMeta';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import TextField from '../../../../framework/fields/TextField.vue';
import TextAreaField from '../../../../framework/fields/TextAreaField.vue';
import { ValidationObserver } from 'vee-validate';
import { Option } from '../../../../../common/framework/model/Option';
import { errorToast, successToast } from '../../../service/toast_service';
import ViewDocuments from './ViewDocuments.vue';
import { Document } from '../../../../../common/application/model/Document';

@Component({
    components: {
        TextField,
        ValidationObserver,
        Multiselect,
        TextAreaField,
        ViewDocuments,
    },
})
export default class AddDocument extends Vue {
    // Properties
    @Prop(String) readonly resourceId!: string;
    @Prop(String) readonly resourceType!: string;
    @Prop(String) readonly customerId!: string;

    $refs!: {
        documents: ViewDocuments;
        multiselect: Multiselect;
    };

    // State
    shared = sharedState;
    local = {
        total: 0,
        loading: false,
        droppedDocuments: null as Document[] | null, //array that contains document objects that user just drop in the page
        categoryOptions: [] as OptionValue[],
        newUploadingDocument: { public: false } as FileMeta, //object that will be uploaded to S3 and file_meta table
        invalidBtn: true,
        categories: [] as string[],
        uploading: false,
    };

    // Functions
    async mounted() {
        this.local.loading = true;
        await this.loadAsyncData();
        this.local.loading = false;
    }

    async loadAsyncData() {
        this.local.newUploadingDocument = {
            ...this.local.newUploadingDocument,
            userId: this.shared.context.userId,
        };

        this.local.categoryOptions = (await getDatabaseOptions('DocumentCategory', this)).filter(
            ({ id }) => id !== undefined,
        );
    }

    async addTag(label: string, document: any) {
        const newOption = {
            id: '',
            type: 'document-category',
            key: label,
            index: 1,
            metadata: {
                labelEn: label,
                labelFi: label,
            },
            vatRate: null,
            created: new Date(),
            modified: new Date(),
        };
        document = { ...document, category: label };
        const option = await postResource<Option>('option', newOption);
        this.local.categoryOptions.push({
            id: option.id,
            label: option.key,
        });
    }

    async add() {
        try {
            if (!this.local.droppedDocuments) {
                return;
            } else {
                for (const [index, document] of this.local.droppedDocuments.entries()) {
                    const documentType = document.name.substring(document.name.lastIndexOf('.') + 1);
                    if (!document.uploadName.includes('.' + documentType)) {
                        document.uploadName = document.uploadName + '.' + documentType;
                    }

                    this.local.newUploadingDocument = {
                        ...this.local.newUploadingDocument,
                        id: '',
                        created: new Date(),
                        modified: new Date(),
                        name: document.uploadName,
                        mimeType: document.type,
                        fileExtension: documentType,
                        size: document.size,
                        category: document.category,
                        description: document.description || '',
                        resourceId: this.resourceId,
                        resourceType: this.resourceType as ApplicationResource,
                        customerId: this.customerId ? this.customerId : null,
                    };
                    const uploadedDocument = await postResource<FileMeta>(
                        ApplicationResource.FILE_META,
                        this.local.newUploadingDocument,
                    );
                    this.upload(uploadedDocument, index);
                }
            }
        } catch (error) {
            console.warn(error);
            errorToast(this);
        }
    }

    @Watch('local.droppedDocuments')
    selectedDocument() {
        if (this.local.droppedDocuments) {
            if (this.local.droppedDocuments.length > this.local.categories.length) {
                this.local.categories.push('Other');
            }
            for (let document of this.local.droppedDocuments) {
                if (!document.uploadName) {
                    document.uploadName = document.name;
                }

                document.uploadName = document.uploadName.normalize('NFC');
            }
        }
    }

    @Watch('local.categories')
    async validateBtn() {
        for (const category of this.local.categories) {
            if (!category) {
                this.local.invalidBtn = true;
                return;
            }
        }
        this.local.invalidBtn = false;
    }

    @Watch('local.categories')
    updateValueAction() {
        if (this.local.droppedDocuments) {
            this.local.droppedDocuments.map((document, index) => (document.category = this.local.categories[index]));
        }
    }

    @Watch('local.uploading')
    async successToastNotification() {
        if (!this.local.uploading) {
            await this.resetPagingAndLoadData();
            await (this.$refs.documents as any).resetPagingAndLoadData();
            successToast(this, 'message.documentSaved');
            return;
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    private async resetPagingAndLoadData() {
        this.local.newUploadingDocument = { public: false } as FileMeta;
        this.local.droppedDocuments = null;
        this.local.categories = [];
        await this.loadAsyncData();
    }

    private upload(uploadedDocument: FileMeta, index: number) {
        this.local.uploading = true;

        try {
            if (this.local.droppedDocuments) {
                const formData = new FormData();
                let file: any = this.local.droppedDocuments[index];
                let name = file.uploadName;
                file = new File([file], name, { type: file.type });

                formData.append('fileBinary', file);
                formData.append('fileId', uploadedDocument.id);
                formData.append('fileName', name);

                const xhr = new XMLHttpRequest();

                xhr.addEventListener('loadend', () => {
                    this.local.uploading = false;
                });

                xhr.addEventListener('error', () => {
                    errorToast(this);
                    this.local.uploading = false;
                });

                xhr.addEventListener('abort', () => {
                    this.local.uploading = false;
                });

                xhr.onreadystatechange = () => {
                    if (xhr.readyState === 4) {
                        if (xhr.status !== 200) {
                            console.warn('Upload returned error status: ' + xhr.status);
                            errorToast(this);
                        }
                    }
                };

                xhr.open(
                    'POST',
                    '/api/file/resource/' + this.resourceType + '/category/' + uploadedDocument.category,
                    true,
                );
                xhr.setRequestHeader('Authorization', 'Bearer ' + sharedState.context.idToken);
                xhr.send(formData);
            }
        } catch (error) {
            console.warn(error);
            errorToast(this);
            this.local.uploading = false;
        }
    }

    async deleteDroppedDocument(deleteDocument: any, index: number) {
        if (this.local.droppedDocuments) {
            if (this.local.droppedDocuments.length === 1) {
                await this.resetPagingAndLoadData();
            }
            this.local.droppedDocuments = this.local.droppedDocuments.filter(
                (document) => document.lastModified !== deleteDocument.lastModified,
            );
            this.local.categories.splice(index, 1);
        }
    }

    getActiveTab(value: number) {
        localStorage.setItem(`${this.resourceType}_ActiveTab`, JSON.stringify(value));
    }
}
